// Toast notifications — ported from legacy toast() semantics:
//  - ONE toast element; a new toast replaces the current one (last wins,
//    clearing the previous hide timer — legacy had no queue and neither do we).
//  - kind '' | 'ok' | anything-else→'err' (legacy: `kind==='ok'?'ok':'err'`).
//  - aria-live: errors are 'assertive', everything else 'polite'.
//  - duration param optional; defaults err 6000ms / other 3000ms.
// The presentation decision is pure (toastPlan) and unit-tested; the DOM part
// is a thin applier that lazily creates its own host element.

import { announce, cancelAnnounce } from './announce.js';

/**
 * Pure presentation plan for a toast. Exact legacy semantics — note the
 * asymmetry, preserved verbatim: the CLASS treats any non-ok truthy kind as
 * err (`kind==='ok'?'ok':'err'`), but aria-live assertive and the 6s duration
 * key off `kind === 'err'` EXACTLY (a hypothetical kind 'warn' looks like an
 * error yet stays polite/3s, as in legacy).
 * @param {string} [kind] '' | 'ok' | 'err' (any non-ok truthy value renders as err)
 * @param {number} [duration] override in ms
 * @returns {{cls: string, ariaLive: 'assertive'|'polite', dur: number, isErr: boolean}}
 */
export function toastPlan(kind = '', duration) {
  const isErr = kind === 'err';
  const cls = 'toast' + (kind ? (kind === 'ok' ? ' ok' : ' err') : '');
  return {
    cls,
    ariaLive: isErr ? 'assertive' : 'polite',
    dur: duration || (isErr ? 6000 : 3000),
    isErr,
  };
}

/** @type {any} */
let tTimer = null;
/** @type {any} */
let showTimer = null;

/** Test-only: drop the pending show/hide timers so a test process can exit.
 *  @returns {void} */
export function _resetToast() {
  clearTimeout(tTimer);
  clearTimeout(showTimer);
  tTimer = null;
  showTimer = null;
}

/**
 * Find or create the singleton toast host.
 * @param {Document} doc
 * @returns {HTMLElement}
 */
function ensureHost(doc) {
  let t = doc.getElementById('toast');
  if (!t) {
    t = doc.createElement('div');
    t.id = 'toast';
    t.className = 'toast';
    t.setAttribute('role', 'status');
    doc.body.appendChild(t);
  }
  return t;
}

/**
 * Show a toast. Thin DOM applier over toastPlan().
 * @param {string} msg plain text (assigned via textContent — never HTML)
 * @param {string} [kind] '' | 'ok' | 'err'
 * @param {number} [duration] override in ms
 * @returns {void}
 */
export function toast(msg, kind = '', duration) {
  const doc = /** @type {any} */ (globalThis).document;
  if (!doc) return; // non-browser context (tests exercise toastPlan directly)
  const plan = toastPlan(kind, duration);
  const t = ensureHost(doc);
  clearTimeout(tTimer);
  clearTimeout(showTimer);
  // Resetting className drops `.show`, which is what restarts the transition
  // for a repeated toast — and since A-06 the hidden state is
  // visibility:hidden, so it ALSO takes the node out of the accessibility
  // tree. That made the old ordering wrong: the text was written here, while
  // the region was out of the tree, and `.show` went back on 10 ms later, so
  // the region re-entered the tree with its content already in place — the
  // one ordering this app's own comments (coachmark.js, auth.js, login.js)
  // name as the way to get silence from NVDA, JAWS and VoiceOver alike.
  // Before A-06 the region was permanently in the tree and every write was a
  // genuine live-region update, so the CSS fix quietly broke the announcement.
  //
  // So: empty it while it is out of the tree, reveal it empty, and write the
  // message after that (announce() puts a real task boundary in — see
  // ui/announce.js for why the reveal and the write cannot share one).
  // The toast is this app's ONLY error channel: every network failure, the
  // owner-join warning, the import result and the CSV-export refusals.
  t.className = plan.cls;
  t.setAttribute('aria-live', plan.ariaLive);
  cancelAnnounce(t);
  t.textContent = '';
  showTimer = setTimeout(() => {
    t.classList.add('show');
    announce(t, msg);
  }, 10);
  tTimer = setTimeout(() => {
    t.classList.remove('show');
    // CSS sets visibility:hidden 180 ms later; empty the text as well so the
    // role="status" node does not keep "Refreshed" in the accessibility tree
    // for a browse-mode user to meet minutes later (A-06).
    tTimer = setTimeout(() => {
      if (!t.classList.contains('show')) {
        cancelAnnounce(t);
        t.textContent = '';
      }
    }, 220);
  }, plan.dur);
}
